import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Table } from 'reactstrap';
import moment from 'moment';
import { environment } from '../../../../environment/environment';
import apiResponseHandler from '../../../../utils/apiResponseHandler';

interface EmployeeBlockAuditLogProps {
  employeeId: string | number;
}

interface BlockAuditEntry {
  id: string;
  timestamp: string;
  actor_email: string | null;
  actor_name: string | null;
  block_type: 'odp_blocked_date' | 'odp_company_blocked_date' | 'segmentation_block';
  action: string;
  block_title: string | null;
  block_reason: string | null;
  start_date: string | null;
  end_date: string | null;
  details: string | null;
}

interface BlockAuditResponse {
  count: number;
  pages: number;
  results: BlockAuditEntry[];
}

const getActionBadgeColor = (action: string): string => {
  switch (action) {
    case 'added':
    case 'created':
      return 'badge-success';
    case 'removed':
    case 'deleted':
      return 'badge-danger';
    case 'modified':
      return 'badge-warning';
    case 'enabled':
    case 'disabled':
      return 'badge-info';
    default:
      return 'badge-secondary';
  }
};

const getBlockTypeLabel = (blockType: string): string => {
  if (blockType === 'odp_blocked_date') return 'Individual Block';
  if (blockType === 'odp_company_blocked_date') return 'Company Block';
  if (blockType === 'segmentation_block') return 'Segmentation Block';
  return blockType;
};

const EmployeeBlockAuditLog: React.FC<EmployeeBlockAuditLogProps> = ({ employeeId }) => {
  const [auditEntries, setAuditEntries] = useState<BlockAuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [employeeId]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    axios
      .get<BlockAuditResponse>(
        `${environment.baseUrl}/odp_limits/blocked_dates/unified_change_log/?employee_id=${employeeId}&page=${page}`,
        { signal: controller.signal }
      )
      .then((res) => {
        setAuditEntries(res.data.results || []);
        setTotalPages(res.data.pages || 1);
        setLoading(false);
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;
        setAuditEntries([]);
        setTotalPages(1);
        setLoading(false);
        if (err?.response?.status !== 404) {
          apiResponseHandler.handleApiError('Error', err);
        }
      });

    return () => controller.abort();
  }, [employeeId, page]);

  return (
    <div>
      <hr className="mt-4 mb-3" />
      <h4 className="mb-3">Block Change History</h4>
      {loading ? (
        <div className="text-center py-3">
          <div className="spinner-border spinner-border-sm" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      ) : auditEntries.length === 0 ? (
        <p className="text-muted text-center">This employee has no block change history</p>
      ) : (
        <>
          <Table className="align-items-center table-flush" responsive>
            <thead className="thead-light">
              <tr>
                <th scope="col">Date</th>
                <th scope="col">Action</th>
                <th scope="col">Type</th>
                <th scope="col">Block Name</th>
                <th scope="col">Reason</th>
                <th scope="col">Changed By</th>
              </tr>
            </thead>
            <tbody>
              {auditEntries.map((entry) => (
                <tr key={entry.id}>
                  <td>{moment(entry.timestamp).format('DD MMM YY h:mm a')}</td>
                  <td>
                    <span className={`badge ${getActionBadgeColor(entry.action)}`}>
                      {entry.action}
                    </span>
                  </td>
                  <td>{getBlockTypeLabel(entry.block_type)}</td>
                  <td>{entry.block_title || '-'}</td>
                  <td>{entry.block_reason || '-'}</td>
                  <td>{entry.actor_name || entry.actor_email || '-'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          {totalPages > 1 && (
            <div className="d-flex justify-content-center align-items-center mt-3">
              <button
                className="btn btn-sm btn-outline-secondary mr-2"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </button>
              <span className="mx-2">
                Page {page} of {totalPages}
              </span>
              <button
                className="btn btn-sm btn-outline-secondary ml-2"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EmployeeBlockAuditLog;
